import React from 'react';
import type { LineItem } from '@Types/cart/LineItem';
import { useWishlist } from '@Provider/frontastic';
import Image from '../../../../../frontastic/lib/image';
import { useFormat } from '../../../../../helpers/hooks/useFormat';
import imageUrlResize from '../../../../../helpers/imageUrlResize';
import Price from '../../../../commercetools-ui/price';
import TrashIcon from '../../../../icons/trash';
import CartItemAttributes from '../../../cart/cart-form/cart-item/attributes';
import classNames from 'classnames';

export interface Props {
  readonly lineItem: LineItem;
  readonly goToProductPage: (_url: string) => void;
  readonly isWishlistItem?: boolean;
}

const LineItem = ({ lineItem, goToProductPage, isWishlistItem = false }: Props) => {
  const { removeLineItem } = useWishlist();
  const { formatMessage } = useFormat({ name: 'common' });

  const onRemove = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    await removeLineItem(lineItem.lineItemId);
  };

  return (
    <div
      className={classNames(
        'relative flex w-full cursor-pointer flex-wrap gap-4 border border-neutral-200 bg-white p-4',
        {
          'pointer-events-none': isWishlistItem && !lineItem?.variant.isOnStock,
        },
      )}
      onClick={() => goToProductPage(lineItem._url)}
    >
      {isWishlistItem && !lineItem?.variant.isOnStock && (
        <>
          <div className="z-20 w-full basis-full bg-white/50">Leider ist dieses Bike nicht mehr verfügbar.</div>
          <div className="absolute left-0 top-0 z-10 h-full w-full bg-white/50"></div>
        </>
      )}

      <div className="flex w-16 shrink-0 items-center md:w-28">
        <Image
          src={imageUrlResize(lineItem.variant.images[0], 'medium')}
          alt={lineItem.name}
          className="rounded-md bg-white"
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-2 md:items-center md:justify-between md:gap-8">
        <div className="flex w-full min-w-0 items-start justify-between gap-2">
          <h3 className="mb-2 min-w-0 font-semibold">
            <span className="block">{lineItem.variant.attributes.brand}</span>
            <span className="block flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
              {lineItem.variant.attributes.model_name}
            </span>
          </h3>
          {isWishlistItem && (
            <button
              type="button"
              onClick={(e) => onRemove(e)}
              className={classNames(
                'pointer-events-auto z-20 inline-flex min-w-0 shrink-0 text-neutral-950 hover:text-gray-500',
                {
                  'absolute right-4 top-4': isWishlistItem && !lineItem?.variant.isOnStock,
                },
              )}
              aria-label={formatMessage({ id: 'remove', defaultMessage: 'Remove' })}
            >
              <TrashIcon className="size-6" aria-hidden="true" />
            </button>
          )}
        </div>
        <div className="flex w-full items-center justify-between">
          <div className="flex flex-wrap">
            <CartItemAttributes
              variant={lineItem.variant}
              attributes={['model_year', 'frame_height_manufacturer', 'mileage_in_km']}
            />
          </div>
          <Price
            price={lineItem.price ?? lineItem.variant?.price}
            className="rounded-4xl bg-accent-300 px-2 text-sm font-bold"
          />
        </div>
      </div>
    </div>
  );
};

export default LineItem;
